/**
 * @fileoverview Boter Kaas en Eieren Cell Component
 * @description Single cell in the Boter Kaas en Eieren game board with animations
 */

import React, { useEffect } from "react";
import {
  TouchableOpacity,
  View,
  Text,
  StyleSheet,
  Animated,
} from "react-native";
import { TicTacToeCellProps } from "../types";

/**
 * TicTacToeCell - A single cell in the game board
 */
export const TicTacToeCell: React.FC<TicTacToeCellProps> = ({
  value,
  position,
  disabled,
  isWinningCell,
  onPress,
  isAnimating,
}) => {
  const scaleAnim = React.useRef(new Animated.Value(0)).current;
  const winPulseAnim = React.useRef(new Animated.Value(1)).current;

  // Animate when a value is placed
  useEffect(() => {
    if (value) {
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 4,
        tension: 150,
        useNativeDriver: true,
      }).start();
    } else {
      scaleAnim.setValue(0);
    }
  }, [value, scaleAnim]);

  // Pulse animation for winning cells
  useEffect(() => {
    if (isWinningCell) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(winPulseAnim, {
            toValue: 1.15,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(winPulseAnim, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      winPulseAnim.setValue(1);
    }
  }, [isWinningCell, winPulseAnim]);

  /**
   * Handle cell press
   */
  const handlePress = () => {
    if (!disabled && !value) {
      onPress();
    }
  };

  /**
   * Get border styles based on position
   */
  const getBorderStyles = () => {
    const styles: Record<string, number> = {};
    const borderWidth = 3;

    // Right border for positions 0, 1, 3, 4, 6, 7
    if (position % 3 !== 2) {
      styles.borderRightWidth = borderWidth;
    }
    // Bottom border for positions 0, 1, 2, 3, 4, 5
    if (position < 6) {
      styles.borderBottomWidth = borderWidth;
    }

    return styles;
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={disabled || value !== null}
      activeOpacity={0.7}
      accessibilityLabel={`Cel ${position + 1}, ${value ? value : "leeg"}`}
      accessibilityHint={
        disabled || value ? undefined : "Dubbeltik om hier te spelen"
      }
      accessibilityRole="button"
      style={[
        styles.cell,
        getBorderStyles(),
        isWinningCell && styles.winningCell,
        isAnimating && styles.animatingCell,
      ]}
    >
      <Animated.View
        style={[
          styles.valueContainer,
          {
            transform: [
              { scale: value ? scaleAnim : 1 },
              { scale: isWinningCell ? winPulseAnim : 1 },
            ],
          },
        ]}
      >
        {value === "X" && (
          <View style={styles.xContainer}>
            <View style={[styles.xLine, styles.xLine1]} />
            <View style={[styles.xLine, styles.xLine2]} />
          </View>
        )}
        {value === "O" && (
          <View style={[styles.oCircle, isWinningCell && styles.oCircleWin]} />
        )}
      </Animated.View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  cell: {
    width: "33.33%",
    aspectRatio: 1,
    justifyContent: "center",
    alignItems: "center",
    borderColor: "#374151",
  },
  winningCell: {
    backgroundColor: "rgba(34, 197, 94, 0.15)",
  },
  animatingCell: {
    backgroundColor: "rgba(139, 92, 246, 0.1)",
  },
  valueContainer: {
    width: "70%",
    height: "70%",
    justifyContent: "center",
    alignItems: "center",
  },
  xContainer: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  xLine: {
    position: "absolute",
    width: "100%",
    height: 8,
    backgroundColor: "#EF4444",
    borderRadius: 4,
  },
  xLine1: {
    transform: [{ rotate: "45deg" }],
  },
  xLine2: {
    transform: [{ rotate: "-45deg" }],
  },
  oCircle: {
    width: "85%",
    height: "85%",
    borderRadius: 100,
    borderWidth: 8,
    borderColor: "#3B82F6",
    backgroundColor: "transparent",
  },
  oCircleWin: {
    borderColor: "#22C55E",
  },
});

export default TicTacToeCell;

